import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Form,
    Button,
    Alert,
    Card,
} from 'react-bootstrap';

import SEO from '@/components/common/SEO';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import { sanitizeRedirectPath } from '@/shared/utils/safeRedirect';
import './LogIn.css';

export default function LogIn() {
    const { ecosystem, isPlatform } = useEcosystem();
    // In tenant mode the destination is that ecosystem; at the apex a sign-in
    // lands on the platform and the chooser routes the user from there.
    const signInDestination =
        isPlatform || !ecosystem?.name ? 'RILE Connect' : ecosystem.name;
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const { login, reloadCurrentUser } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // sanitizeRedirectPath returns null for any non-same-origin value
    // (protocol-relative, scheme URLs, whitespace tricks). When null, we fall
    // through to the chooser hand-off instead of trusting attacker-controlled
    // input. The "default to /dashboard" we used to do here was the bug Wave
    // K replaces: /dashboard is tenant-scoped and renders against null
    // context at the apex.
    const redirectTo = sanitizeRedirectPath(searchParams.get('redirect'));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const userCredential = await login(email, password);
            if (!userCredential.user.emailVerified) {
                setError(
                    'Please verify your email before logging in. Check your inbox for a verification link.'
                );
                setLoading(false);
                return;
            }
            // Ensure auth context is updated before navigating
            await reloadCurrentUser();
            // If the user came in via a sanitized `?redirect=`, honor it,
            // they clicked a deep link to a specific page and we should
            // respect their intent. Otherwise hand off through
            // /choose-ecosystem, which resolves 0 / 1 / N memberships and
            // either welcomes the user, auto-redirects to their single
            // tenant, or shows the picker.
            navigate(redirectTo || '/choose-ecosystem');
        } catch (err) {
            setError(
                err.code === 'auth/invalid-credential'
                    ? 'Invalid email or password'
                    : 'Failed to log in. Please try again.'
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SEO
                title="Log In"
                description={`Sign in to your RILE Connect account to access ${signInDestination}.`}
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Log In', path: '/login' },
                ]}
            />
            <Container className="login-container py-5">
                <Row className="justify-content-center">
                    <Col md={6} lg={5}>
                        <Card className="shadow-sm">
                            <Card.Body className="p-4">
                                <h2 className="text-center text-primary mb-4">
                                    Welcome Back
                                </h2>

                                {error && (
                                    <Alert variant="danger">{error}</Alert>
                                )}

                                <Form onSubmit={handleSubmit}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            value={email}
                                            onChange={(e) =>
                                                setEmail(e.target.value)
                                            }
                                            placeholder="Enter your email"
                                            required
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Password</Form.Label>
                                        <Form.Control
                                            type="password"
                                            value={password}
                                            onChange={(e) =>
                                                setPassword(e.target.value)
                                            }
                                            placeholder="Enter your password"
                                            required
                                        />
                                    </Form.Group>

                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <Link
                                            to="/forgot-password"
                                            className="text-primary"
                                        >
                                            Forgot password?
                                        </Link>
                                    </div>

                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="w-100 rounded-pill"
                                        disabled={loading}
                                    >
                                        {loading ? 'Logging in...' : 'Login'}
                                    </Button>
                                </Form>

                                <div className="text-center mt-4">
                                    <p>
                                        Don&apos;t have an account?{' '}
                                        <Link
                                            to="/signup"
                                            className="text-primary fw-medium"
                                        >
                                            Sign Up
                                        </Link>
                                    </p>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}
